import type { AuditReport } from "@/types/audit";
import { computeOverallScore, deriveScoreLabel } from "./score";
// Refactor B+D (2026-06-17): canonical list of scan failure reasons lives in
// the shared labels module so the email template, web report and runtime
// validator can't drift. The parity test asserts every reason here has a
// matching SCAN_FAILURE_LABELS entry.
import {
  SCAN_FAILURE_REASONS as SHARED_SCAN_FAILURE_REASONS,
  isScanFailureReason as sharedIsScanFailureReason,
  type ScanFailureReason as SharedScanFailureReason,
} from "../../supabase/functions/_shared/audit/labels";

/**
 * Single place that turns the raw n8n response into the `AuditReport`
 * shape the UI consumes. Lives outside the page components so it can be
 * unit-tested without React, and so the cache-hit path, the polling
 * path, and the cold-localStorage hydrate in `Analyse.tsx` all share
 * exactly the same logic.
 *
 * Two non-trivial things happen here:
 *   1. `[FAIL:reason]` legacy prefix on `fetchWarning` is stripped and
 *      promoted to a structured `scanFailureReason`.
 *   2. The aggregate score is recomputed from the per-section scores via
 *      our canonical formula (`lib/score.ts`). The n8n envelope `score`
 *      is ignored, because empirically it disagrees with both its own
 *      `htmlReport` gauge and any defensible weighted mean.
 */

// Re-export the canonical union under the names the rest of this module
// expected. Keeps every other call site in this file unchanged.
const SCAN_FAILURE_REASONS = SHARED_SCAN_FAILURE_REASONS;
type ScanFailureReason = SharedScanFailureReason;
const isScanFailureReason = sharedIsScanFailureReason;

/**
 * Strips the legacy `[FAIL:reason]` prefix from a fetchWarning string
 * and returns the cleaned text. The prefix encoding was used by older
 * n8n workflow versions before they had a dedicated scanFailureReason
 * column.
 */
export const stripFailPrefix = (warning: string | null | undefined): string => {
  if (!warning) return "";
  return warning.replace(/^\[FAIL:[a-z_]+\]\s*/, "");
};

/**
 * Reads the structured scanFailureReason if the workflow set one,
 * otherwise falls back to parsing the legacy prefix from fetchWarning.
 * Returns null when nothing is set.
 */
export const parseExplicitScanFailureReason = (
  raw: { scanFailureReason?: unknown; fetchWarning?: string | null } | null | undefined,
): ScanFailureReason | null => {
  if (!raw) return null;
  const direct = raw.scanFailureReason;
  if (typeof direct === "string" && isScanFailureReason(direct)) {
    return direct;
  }
  const prefix = (raw.fetchWarning || "").match(/^\[FAIL:([a-z_]+)\]/);
  if (prefix && isScanFailureReason(prefix[1])) {
    return prefix[1];
  }
  return null;
};

/**
 * Whether any non-NA section produced a score >= the given threshold.
 * Used as a guard before promoting a soft fetchWarning to a hard
 * `client_side_render` failure: if at least one category found real
 * material, we don't want to hide the score behind the "Aucun score"
 * banner.
 */
const SCORE_THRESHOLD_FOR_SIGNAL = 20;

export const hasScoreableSignal = (
  sections: AuditReport["sections"] | undefined,
): boolean => {
  if (!Array.isArray(sections)) return false;
  return sections.some(
    (s) =>
      !s.isNA &&
      typeof s.score === "number" &&
      (s.score ?? 0) >= SCORE_THRESHOLD_FOR_SIGNAL,
  );
};

/**
 * Promote a soft `fetchWarning` to a hard `scanFailureReason` when the
 * text matches a known SPA-shell pattern AND no section produced
 * scoreable signal. Currently only covers the SPA / client-side-render
 * case — the n8n workflow doesn't have a dedicated enum slot for it.
 *
 * Caveat: the workflow currently emits the SPA warning indiscriminately
 * (server-rendered law firms get it too). We rely on the
 * `hasScoreableSignal` gate to avoid hiding good results.
 */
export const inferScanFailureReason = (
  existing: ScanFailureReason | null | undefined,
  fetchWarning: string,
  sections: AuditReport["sections"] | undefined,
): ScanFailureReason | null => {
  if (existing) return existing;
  const w = (fetchWarning || "").toLowerCase();
  const looksLikeSpa =
    w.includes("côté client") ||
    w.includes("contenu réduit") ||
    w.includes("client-side") ||
    w.includes("spa") ||
    w.includes("squelette html");
  if (looksLikeSpa && !hasScoreableSignal(sections)) {
    return "client_side_render";
  }
  return null;
};

/**
 * Recompute the aggregate score from per-section scores using the
 * canonical formula. Only mutates score + scoreLabel; everything else
 * passes through.
 *
 * Phase A.2 (Mistral migration, 2026-06): when the report carries
 * `score === null` (new shape — the orchestrator emitted no overall
 * score and section.score is also null), do NOT recompute. Forcing a 0
 * here would defeat the renderer's `scoreRetired` detection (which keys
 * off `report.score == null`).
 */
export const reconcileReportScore = (report: AuditReport): AuditReport => {
  if (!Array.isArray(report.sections) || report.sections.length === 0) {
    return report;
  }
  // New-shape detection: no overall score AND no section carries a number.
  // computeOverallScore() would collapse this to 0; preserve null instead so
  // the renderer can hide the gauge.
  const noSectionScores = report.sections.every(
    (s) => s.isNA || typeof s.score !== "number",
  );
  if (report.score == null && noSectionScores) {
    return report;
  }
  const score = computeOverallScore(report.sections);
  return {
    ...report,
    score,
    scoreLabel: deriveScoreLabel(score),
  };
};

/**
 * Full pipeline: takes whatever n8n returned (loose Partial<AuditReport>
 * with extras), produces the typed AuditReport the UI uses.
 *
 * `fallbackUrl` and `fallbackReportId` are used when the n8n payload is
 * missing those fields (it sometimes is on cache hits).
 */
export const buildAuditReport = (
  raw: Partial<AuditReport> & { status?: string; scanFailureReason?: unknown },
  fallbacks: { url: string; reportId: string },
): AuditReport => {
  const cleanedWarning = stripFailPrefix(raw.fetchWarning);
  const explicit = parseExplicitScanFailureReason(raw);
  const inferred = inferScanFailureReason(
    explicit,
    cleanedWarning,
    raw.sections,
  );

  // Preserve scanFailureLanguage when present. Only meaningful when
  // scanFailureReason === "unsupported_language" but stored unconditionally
  // so the wire-passthrough is uniform. Narrow to the known locale set
  // (matches Language in shared/audit/types.ts) so an arbitrary backend
  // string can't leak into the UI's language-name map.
  const rawLang = (raw as { scanFailureLanguage?: unknown }).scanFailureLanguage;
  const scanFailureLanguage: AuditReport["scanFailureLanguage"] =
    typeof rawLang === "string" &&
    ["en", "de", "it", "rm", "fr", "unknown"].includes(rawLang)
      ? (rawLang as AuditReport["scanFailureLanguage"])
      : null;

  // Preserve rateLimitRetryAfter. Only set when Index.tsx synthesises a
  // rate_limited AuditReport from a backend 429; pass through verbatim.
  const rawRetry = (raw as { rateLimitRetryAfter?: unknown }).rateLimitRetryAfter;
  const rateLimitRetryAfter: number | null =
    typeof rawRetry === "number" && rawRetry > 0 ? rawRetry : null;

  // 2026-05-14: pass behavioral fields through. Pre-fix these were silently
  // dropped here — the wire had findings/status/summary but they never
  // reached AuditResults, so the "Comportement du site" section never
  // rendered no matter what the backend captured. The behavioral feature
  // requires these to round-trip; ecoute.ch with 6 cookies + 7 Wix domains
  // was showing as having no behavioral data because of this drop.
  const rawFindings = raw.findings;
  const findings = Array.isArray(rawFindings) ? rawFindings : undefined;
  const rawStatus = (raw as { behavioralScanStatus?: unknown }).behavioralScanStatus;
  const behavioralScanStatus: AuditReport["behavioralScanStatus"] =
    rawStatus === "ok" || rawStatus === "partial" || rawStatus === "unavailable"
      ? rawStatus
      : null;
  const rawSummary = (raw as { behavioralSummary?: unknown }).behavioralSummary;
  const behavioralSummary: AuditReport["behavioralSummary"] =
    rawSummary && typeof rawSummary === "object" ? (rawSummary as AuditReport["behavioralSummary"]) : undefined;

  // Fields the server populates but buildAuditReport historically dropped
  // (owner-spotted on nlpd.jagerdori.com 2026-05-20: free-scan-status
  // correctly returned forensicsInventory with 3 trackers, but the legacy
  // "Aucun cookie ni domaine tiers observé" fallback rendered because the
  // deserializer never copied the field into the AuditReport object).
  // Pass through verbatim — types.ts marks each as optional with the right
  // shape, so untyped JSON deserializes cleanly.
  const rawForensicsInventory = (raw as { forensicsInventory?: unknown }).forensicsInventory;
  const forensicsInventory: AuditReport["forensicsInventory"] =
    rawForensicsInventory && typeof rawForensicsInventory === "object"
      ? (rawForensicsInventory as AuditReport["forensicsInventory"])
      : undefined;
  const rawDocClass = (raw as { documentClassification?: unknown }).documentClassification;
  const documentClassification: AuditReport["documentClassification"] =
    rawDocClass && typeof rawDocClass === "object"
      ? (rawDocClass as AuditReport["documentClassification"])
      : undefined;
  const rawHasForcedFallback = (raw as { hasForcedFallback?: unknown }).hasForcedFallback;
  const hasForcedFallback: AuditReport["hasForcedFallback"] =
    typeof rawHasForcedFallback === "boolean" ? rawHasForcedFallback : undefined;

  // Phase A.1 (Mistral migration, 2026-06): preserve the optional
  // policyFlagReason / policyFlagMessage emitted by free-scan/index.ts when
  // a deterministic precheck fired. Narrow the reason to the known union so
  // junk strings can't leak through.
  const rawPolicyFlagReason = (raw as { policyFlagReason?: unknown }).policyFlagReason;
  const policyFlagReason: AuditReport["policyFlagReason"] =
    typeof rawPolicyFlagReason === "string" &&
    ["POLICY_TOO_SHORT", "POLICY_TOO_LONG", "WRONG_LANGUAGE", "NOT_A_POLICY"].includes(
      rawPolicyFlagReason,
    )
      ? (rawPolicyFlagReason as AuditReport["policyFlagReason"])
      : undefined;
  const rawPolicyFlagMessage = (raw as { policyFlagMessage?: unknown }).policyFlagMessage;
  const policyFlagMessage: AuditReport["policyFlagMessage"] =
    typeof rawPolicyFlagMessage === "string" && rawPolicyFlagMessage.length > 0
      ? rawPolicyFlagMessage
      : undefined;

  // Phase A.2 (Mistral migration, 2026-06): preserve `null`/`undefined`
  // overall score for new-shape reports. The previous default of `0`
  // defeats the renderer's `scoreRetired` detection (which keys on
  // `report.score == null`).
  const rawScoreVal = (raw as { score?: unknown }).score;
  const preservedScore: AuditReport["score"] =
    typeof rawScoreVal === "number"
      ? rawScoreVal
      : rawScoreVal === null
      ? null
      : undefined;
  const rawScoreLabel = (raw as { scoreLabel?: unknown }).scoreLabel;
  const preservedScoreLabel: AuditReport["scoreLabel"] =
    typeof rawScoreLabel === "string"
      ? rawScoreLabel
      : rawScoreLabel === null
      ? null
      : undefined;

  const base: AuditReport = {
    reportId: raw.reportId ?? fallbacks.reportId,
    url: raw.url ?? fallbacks.url,
    email: raw.email ?? null,
    score: preservedScore,
    scoreLabel: preservedScoreLabel,
    sections: Array.isArray(raw.sections) ? raw.sections : [],
    htmlReport: raw.htmlReport ?? "",
    reportUrl: raw.reportUrl ?? null,
    fetchBlocked: raw.fetchBlocked,
    fetchWarning: cleanedWarning,
    completedAt: raw.completedAt ?? null,
    scanFailureReason: inferred,
    scanFailureLanguage,
    rateLimitRetryAfter,
    findings,
    behavioralScanStatus,
    behavioralSummary,
    forensicsInventory,
    documentClassification,
    hasForcedFallback,
    policyFlagReason,
    policyFlagMessage,
  };

  return reconcileReportScore(base);
};
